import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type UserNetworkingAccessJSON = z.infer<typeof UserNetworkingAccessSchema.UserNetworkingAccessJSONZ>;

export type UserNetworkingAccessParams = z.infer<typeof UserNetworkingAccessSchema.UserNetworkingAccessZ>;

export default class UserNetworkingAccessSchema {
  public static readonly UserNetworkingAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.UserNetworkingAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly UserNetworkingAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.UserNetworkingAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): UserNetworkingAccessParams {
    return parseOrThrow(UserNetworkingAccessSchema.UserNetworkingAccessZ, values, {
      logError: true,
      schemaName: "UserNetworkingAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): UserNetworkingAccessJSON {
    return parseOrThrow(UserNetworkingAccessSchema.UserNetworkingAccessJSONZ, values, {
      logError: true,
      schemaName: "UserNetworkingAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is UserNetworkingAccessParams {
    try {
      UserNetworkingAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is UserNetworkingAccessJSON {
    try {
      UserNetworkingAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: UserNetworkingAccessJSON): UserNetworkingAccessParams {
    const parsed = parseOrThrow(UserNetworkingAccessSchema.UserNetworkingAccessJSONZ, data, {
      logError: true,
      schemaName: "UserNetworkingAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return UserNetworkingAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: UserNetworkingAccessParams): UserNetworkingAccessJSON {
    const parsed = UserNetworkingAccessSchema.validate(notification);
    return UserNetworkingAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    UserNetworkingAccessSchema.UserNetworkingAccessJSONZ,
    UserNetworkingAccessSchema.UserNetworkingAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.UserNetworkingAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.UserNetworkingAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
